import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { DragDropContext, Draggable, DropResult, DraggableProvided, DraggableStateSnapshot, DroppableProvided } from 'react-beautiful-dnd';
import { Button, Icon, Tag, Menu, MenuItem, Dialog, InputGroup, FormGroup, Intent, ContextMenu, Collapse, Card, Elevation, Tooltip, EditableText } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import { useAppStore } from '../../stores/UnifiedStoreProvider';
import { formatDistanceToNow } from 'date-fns';
import { StrictModeDroppable } from '../common/StrictModeDroppable';
import './MindmapSidebarDnD.css';

interface Props {
  onSelect: (id: string) => void;
  activeMindmapId?: string;
  onToggleCollapse?: () => void;
}

interface MindmapCardProps {
  mindmap: any;
  active?: boolean;
  onClick: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onToggleVisibility?: () => void;
  onRename?: (newName: string) => void;
}

interface FolderProps {
  folder: any;
  mindmaps: any[];
  activeMindmapId?: string;
  onSelectMindmap: (id: string) => void;
  onRenameFolder: () => void;
  onDeleteFolder: () => void;
  onDeleteMindmap: (id: string) => void;
  onDuplicateMindmap: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onRenameMindmap: (id: string, name: string) => void;
}

// Section header
const SectionHeader: React.FC<{ title: string; count: number; onAdd?: () => void }> = ({ 
  title, 
  count, 
  onAdd 
}) => (
  <div className="mindmap-section-header">
    <div className="section-title-group">
      <span className="section-title">{title}</span>
    </div>
    {onAdd && (
      <Button 
        icon={IconNames.PLUS} 
        minimal 
        small 
        onClick={(e) => {
          e.stopPropagation();
          onAdd();
        }}
        className="section-add-btn"
        title={`Create ${title} Mindmap`}
      />
    )}
  </div>
);

// Mindmap card with metadata in tooltip and inline editing
const MindmapCard: React.FC<MindmapCardProps> = ({ 
  mindmap, 
  active, 
  onClick,
  onDelete,
  onDuplicate,
  onToggleVisibility,
  onRename
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(mindmap.name || 'Untitled Mindmap');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPopoverOpen(false);
    setIsEditing(true);
    setEditedName(mindmap.name || 'Untitled Mindmap');
  };

  const handleRenameConfirm = () => {
    if (editedName.trim() && editedName !== mindmap.name && onRename) {
      // Call the parent's rename handler with the new name
      onRename(editedName);
    }
    setIsEditing(false);
  };

  const handleRenameCancel = () => {
    setEditedName(mindmap.name || 'Untitled Mindmap');
    setIsEditing(false);
  };

  // Build tooltip content
  const tooltipContent = (
    <div style={{ fontSize: '11px', lineHeight: '1.5', maxWidth: '300px' }}>
      {mindmap.description && (
        <div><strong>Description:</strong> {mindmap.description}</div>
      )}
      {mindmap.nodeCount !== undefined && (
        <div><strong>Nodes:</strong> {mindmap.nodeCount}</div>
      )}
      {mindmap.updatedAt && (
        <div><strong>Last updated:</strong> {formatDistanceToNow(new Date(mindmap.updatedAt), { addSuffix: true })}</div>
      )}
      {mindmap.visibility && (
        <div><strong>Visibility:</strong> {mindmap.visibility}</div>
      )}
    </div>
  );

  const actionMenu = (
    <Menu>
      <MenuItem 
        icon={IconNames.EDIT} 
        text="Rename" 
        onClick={handleRenameClick}
      />
      <MenuItem 
        icon={IconNames.DUPLICATE} 
        text="Duplicate" 
        onClick={(e) => {
          e.stopPropagation();
          setIsPopoverOpen(false);
          onDuplicate?.();
        }}
      />
      <MenuItem 
        icon={mindmap.visibility === 'shared' ? IconNames.LOCK : IconNames.UNLOCK} 
        text={mindmap.visibility === 'shared' ? 'Make Private' : 'Make Shared'}
        onClick={(e) => {
          e.stopPropagation();
          setIsPopoverOpen(false);
          onToggleVisibility?.();
        }}
      />
      <MenuItem 
        icon={IconNames.TRASH} 
        text="Delete" 
        intent={Intent.DANGER}
        onClick={(e) => {
          e.stopPropagation();
          setIsPopoverOpen(false);
          onDelete?.();
        }}
      />
    </Menu>
  );

        return (
          <Tooltip content={tooltipContent} placement="right" hoverOpenDelay={500} disabled={isEditing || isPopoverOpen}>
            <div className={`mindmap-card ${active ? 'active' : ''}`}>
              {isEditing ? (
                <EditableText
                  value={editedName}
                  onChange={setEditedName}
                  onConfirm={handleRenameConfirm}
                  onCancel={handleRenameCancel}
                  selectAllOnFocus
                  placeholder="Mindmap name..."
                  className="mindmap-title-edit"
                />
              ) : (
                <div className="mindmap-title" onClick={onClick}>
                  {mindmap.name || 'Untitled Mindmap'}
                </div>
              )}
              <Popover2
                content={actionMenu}
                placement="right-start"
                isOpen={isPopoverOpen}
                onInteraction={(nextState) => setIsPopoverOpen(nextState)}
              >
                <Button
                  icon={IconNames.MORE}
                  minimal
                  small
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsPopoverOpen(!isPopoverOpen);
                  }}
                  className="mindmap-card-actions"
                />
              </Popover2>
            </div>
          </Tooltip>
  );
};

// Folder component
const FolderComponent: React.FC<FolderProps> = ({
  folder,
  mindmaps,
  activeMindmapId,
  onSelectMindmap,
  onRenameFolder,
  onDeleteFolder,
  onDeleteMindmap,
  onDuplicateMindmap,
  onToggleVisibility,
  onRenameMindmap
}) => {
  const handleFolderContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    ContextMenu.show(
      <Menu>
        <MenuItem icon={IconNames.EDIT} text="Rename" onClick={onRenameFolder} />
        <MenuItem 
          icon={IconNames.TRASH} 
          text="Delete" 
          intent={Intent.DANGER}
          onClick={onDeleteFolder}
        />
      </Menu>,
      { left: e.clientX, top: e.clientY }
    );
  };

  return (
    <StrictModeDroppable droppableId={`folder:${folder.id}`}>
      {(provided: DroppableProvided, snapshot) => (
        <div className={`folder-container ${snapshot.isDraggingOver ? 'drag-over' : ''}`}>
          <div 
            className="folder-header" 
            onContextMenu={handleFolderContextMenu}
          >
            <Icon 
              icon={IconNames.FOLDER_OPEN} 
              size={14} 
              style={{ opacity: 0.5, color: '#6b7280' }}
            />
            <span className="folder-name">{folder.name}</span>
          </div>
          
          <div 
            ref={provided.innerRef} 
            {...provided.droppableProps}
            className="folder-mindmaps"
          >
            {mindmaps.map((mindmap, index) => (
              <Draggable key={mindmap.id} draggableId={mindmap.id} index={index}>
                {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={snapshot.isDragging ? 'dragging' : ''}
                  >
                    <MindmapCard
                      mindmap={mindmap}
                      active={activeMindmapId === mindmap.id}
                      onClick={() => onSelectMindmap(mindmap.id)}
                      onDelete={() => onDeleteMindmap(mindmap.id)}
                      onDuplicate={() => onDuplicateMindmap(mindmap.id)}
                      onToggleVisibility={() => onToggleVisibility(mindmap.id)}
                      onRename={(newName) => onRenameMindmap(mindmap.id, newName)}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        </div>
      )}
    </StrictModeDroppable>
  );
};

// Main component
const MindmapSidebarDnD: React.FC<Props> = observer(({ onSelect, activeMindmapId, onToggleCollapse }) => {
  const appStore = useAppStore();
  const { organization } = appStore;
  const [createDialog, setCreateDialog] = useState(false);
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [mindmapName, setMindmapName] = useState('');
  const [mindmapDescription, setMindmapDescription] = useState('');
  const [mindmapVisibility, setMindmapVisibility] = useState<'private' | 'shared'>('private');
  const [folderName, setFolderName] = useState('');
  const [folderType, setFolderType] = useState<'private' | 'shared'>('private');
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; id: string; name: string; isFolder: boolean }>({ 
    open: false, 
    id: '', 
    name: '',
    isFolder: false 
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Load mindmaps on mount if not already loaded
  useEffect(() => {
    console.log('[MindmapSidebarDnD] Component mounted/updated:');
    console.log('  - Organization ID:', organization.organizationId);
    console.log('  - Current Organization:', organization.currentOrganization?.id);
    console.log('  - Is Loading:', organization.isLoading);
    console.log('  - Error:', organization.error);
    console.log('  - Mindmaps count:', organization.mindmaps.length);
    
    if (organization.mindmaps.length > 0) {
      console.log('  ✅ Mindmaps available:', organization.mindmaps.map((m: any) => ({
        id: m.id,
        name: m.name
      })));
    } else {
      console.warn('  ⚠️ No mindmaps in store!');
      // Don't auto-load here - prevents infinite loops when load fails with 401
      // Parent component (UnifiedAppInitializer) handles initial data loading
    }
  }, [organization.mindmaps.length, organization.isLoading, organization.organizationId]);

  // Filter mindmaps
  // Note: Keep MST observables reactive - don't use .slice() which loses reactivity
  // The observer() wrapper will track accesses to mindmap properties (including nodeCount)
  const filteredMindmaps = useMemo(() => {
    const mindmaps = organization.mindmaps; // Keep as MST observable array
    if (!searchQuery) return mindmaps.slice(); // Only slice at the end for the return value
    const query = searchQuery.toLowerCase();
    return mindmaps.filter((m: any) => 
      m.name?.toLowerCase().includes(query) || 
      m.description?.toLowerCase().includes(query)
    );
  }, [organization.mindmaps, searchQuery]); // Depend on the full observable array, not just .length

  const privateMindmaps = useMemo(() => 
    filteredMindmaps.filter((m: any) => m.visibility !== 'shared'), 
    [filteredMindmaps]
  );
  
  const sharedMindmaps = useMemo(() => 
    filteredMindmaps.filter((m: any) => m.visibility === 'shared'), 
    [filteredMindmaps]
  );

  // Handle drag end
  const onDragEnd = useCallback(async (result: DropResult) => {
    console.log('[MindmapSidebarDnD] ========== DRAG END ==========');
    console.log('[MindmapSidebarDnD] Full result:', JSON.stringify(result, null, 2));
    console.log('[MindmapSidebarDnD] Draggable ID:', result.draggableId);
    console.log('[MindmapSidebarDnD] Source:', {
      droppableId: result.source?.droppableId,
      index: result.source?.index
    });
    console.log('[MindmapSidebarDnD] Destination:', {
      droppableId: result.destination?.droppableId,
      index: result.destination?.index
    });
    
    if (!result.destination) {
      console.log('[MindmapSidebarDnD] ❌ No destination, cancelling drag');
      return;
    }
    
    const { draggableId, source, destination } = result;
    
    // Don't do anything if dropped in same position
    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      console.log('[MindmapSidebarDnD] ⚠️ Same position, no change needed');
      return;
    }

    const mindmap = organization.mindmaps.find((m: any) => m.id === draggableId);
    if (!mindmap) {
      console.error('[MindmapSidebarDnD] ❌ Mindmap not found:', draggableId);
      console.error('[MindmapSidebarDnD] Available mindmaps:', organization.mindmaps.map((m: any) => ({id: m.id, name: m.name})));
      return;
    }

    console.log('[MindmapSidebarDnD] ✅ Found mindmap:', {
      id: mindmap.id,
      name: mindmap.name,
      currentVisibility: mindmap.visibility,
      currentFolderId: mindmap.folderId
    });
    console.log('[MindmapSidebarDnD] 📍 Moving from:', source.droppableId, 'index:', source.index);
    console.log('[MindmapSidebarDnD] 📍 Moving to:', destination.droppableId, 'index:', destination.index);

    try {
      // Determine source and destination contexts
      const isFromFolder = source.droppableId.startsWith('folder:');
      const isToFolder = destination.droppableId.startsWith('folder:');
      const sourceFolderId = isFromFolder ? source.droppableId.replace('folder:', '') : null;
      const destFolderId = isToFolder ? destination.droppableId.replace('folder:', '') : null;

      console.log('[MindmapSidebarDnD] 🔍 Context analysis:', {
        isFromFolder,
        isToFolder,
        sourceFolderId,
        destFolderId
      });

      // Handle all drag scenarios
      if (isToFolder) {
        // SCENARIO: Dragging TO a folder (from anywhere)
        console.log('[MindmapSidebarDnD] 📂 SCENARIO: Moving to folder:', destFolderId);
        
        // Find the destination folder to get its type
        const destFolder = organization.folders.find((f: any) => f.id === destFolderId);
        console.log('[MindmapSidebarDnD] Destination folder details:', destFolder);
        
        if (destFolder) {
          // If moving between different folders
          if (isFromFolder && sourceFolderId !== destFolderId) {
            console.log('[MindmapSidebarDnD] Moving between folders:', sourceFolderId, '->', destFolderId);
          } else if (!isFromFolder) {
            console.log('[MindmapSidebarDnD] Moving from root to folder');
          }
          
          console.log('[MindmapSidebarDnD] 🎯 Moving mindmap to folder:', destFolderId);
          // Move to folder and set visibility based on folder type
          await organization.moveMindmapToFolder(mindmap.id, destFolderId);
          console.log('[MindmapSidebarDnD] ✅ Moved to folder successfully');
          
          // Set visibility to match the folder's type
          if (mindmap.visibility !== destFolder.type) {
            console.log('[MindmapSidebarDnD] 🔄 Updating visibility from', mindmap.visibility, 'to', destFolder.type);
            await organization.setMindmapVisibility(mindmap.id, destFolder.type);
            console.log('[MindmapSidebarDnD] ✅ Visibility updated');
          }
        }
      } 
      else if (destination.droppableId === 'private' || destination.droppableId === 'shared') {
        // SCENARIO: Dragging to root private/shared sections
        const newVisibility = destination.droppableId as 'private' | 'shared';
        console.log('[MindmapSidebarDnD] 🏠 SCENARIO: Moving to root section:', newVisibility);
        
        if (isFromFolder) {
          // Moving OUT of a folder to root
          console.log('[MindmapSidebarDnD] 📤 Moving OUT of folder to', newVisibility, 'root');
          
          // First remove from folder
          console.log('[MindmapSidebarDnD] 🎯 Removing from folder');
          await organization.moveMindmapToFolder(mindmap.id, null);
          console.log('[MindmapSidebarDnD] ✅ Removed from folder');
          
          // Then update visibility if different
          if (mindmap.visibility !== newVisibility) {
            console.log('[MindmapSidebarDnD] 🔄 Updating visibility from', mindmap.visibility, 'to', newVisibility);
            await organization.setMindmapVisibility(mindmap.id, newVisibility);
            console.log('[MindmapSidebarDnD] ✅ Visibility updated');
          }
        } else {
          // Moving between root sections or reordering within same section
          if (source.droppableId !== destination.droppableId) {
            console.log('[MindmapSidebarDnD] Changing visibility from', mindmap.visibility, 'to:', newVisibility);
            await organization.setMindmapVisibility(mindmap.id, newVisibility);
          } else {
            // Same section - just reordering
            console.log('[MindmapSidebarDnD] Reordering within', newVisibility, 'section');
            console.log('[MindmapSidebarDnD] From index:', source.index, 'To index:', destination.index);
            // TODO: Implement position/order persistence if needed
            // await organization.reorderMindmap(mindmap.id, destination.index);
          }
        }
      }
      console.log('[MindmapSidebarDnD] ✅ Successfully completed drag operation');
      console.log('[MindmapSidebarDnD] ========== END DRAG ==========\n');
    } catch (error) {
      console.error('[MindmapSidebarDnD] ❌❌❌ Error updating mindmap:', error);
      console.error('[MindmapSidebarDnD] Error details:', {
        message: error.message,
        stack: error.stack,
        mindmap,
        source,
        destination
      });
    }
  }, [organization]);

  // Create mindmap
  const handleCreateMindmap = useCallback(async () => {
    if (!mindmapName.trim()) return;
    try {
      console.log('[MindmapSidebarDnD] Creating mindmap:', mindmapName);
      const created = await organization.createMindmap(
        mindmapName,
        mindmapDescription || undefined,
        undefined,
        mindmapVisibility
      );
      console.log('[MindmapSidebarDnD] Mindmap created:', created);
      
      if (created?.id) {
        // Close dialog and reset form
        setCreateDialog(false);
        setMindmapName('');
        setMindmapDescription('');
        setMindmapVisibility('private');
        
        // Select the new mindmap
        onSelect(created.id);
        
        // Force a refresh to ensure the list is updated
        console.log('[MindmapSidebarDnD] Refreshing mindmap list...');
        await organization.loadMindmaps();
      }
    } catch (error) {
      console.error('[MindmapSidebarDnD] Failed to create mindmap:', error);
    }
  }, [organization, mindmapName, mindmapDescription, mindmapVisibility, onSelect]);

  // Quick create mindmap
  const handleQuickCreate = useCallback(async (visibility: 'private' | 'shared') => {
    try {
      const name = `New Mindmap ${new Date().toLocaleTimeString()}`;
      console.log('[MindmapSidebarDnD] Quick creating mindmap:', name, visibility);
      const created = await organization.createMindmap(name, undefined, undefined, visibility);
      console.log('[MindmapSidebarDnD] Quick created:', created);
      
      if (created?.id) {
        onSelect(created.id);
        // Force refresh to show the new mindmap
        await organization.loadMindmaps();
      }
    } catch (error) {
      console.error('[MindmapSidebarDnD] Failed to quick create mindmap:', error);
    }
  }, [organization, onSelect]);

  // Create folder
  const handleCreateFolder = useCallback(async () => {
    if (!folderName.trim()) return;
    await organization.createFolder(folderName, folderType);
    setCreateFolderDialog(false);
    setFolderName('');
  }, [organization, folderName, folderType]);

  // Delete mindmap
  const handleDeleteMindmap = useCallback(async (id: string) => {
    if (confirm('Delete this mindmap? This cannot be undone.')) {
      await organization.deleteMindmap(id);
    }
  }, [organization]);

  // Duplicate mindmap
  const handleDuplicateMindmap = useCallback(async (id: string) => {
    const mindmap = organization.mindmaps.find((m: any) => m.id === id);
    if (mindmap) {
      const duplicated = await organization.duplicateMindmap(id);
      if (duplicated?.id) onSelect(duplicated.id);
    }
  }, [organization, onSelect]);

  // Toggle visibility
  const handleToggleVisibility = useCallback(async (id: string) => {
    const mindmap = organization.mindmaps.find((m: any) => m.id === id);
    if (mindmap) {
      await organization.setMindmapVisibility(
        id,
        mindmap.visibility === 'shared' ? 'private' : 'shared'
      );
    }
  }, [organization]);

  // Rename
  const handleRename = useCallback(async () => {
    if (!renameDialog.name.trim()) return;
    
    if (renameDialog.isFolder) {
      await organization.updateFolder(renameDialog.id, { name: renameDialog.name });
    } else {
      await organization.renameMindmap(renameDialog.id, renameDialog.name);
    }
    
    setRenameDialog({ open: false, id: '', name: '', isFolder: false });
  }, [organization, renameDialog]);

  // Delete folder
  const handleDeleteFolder = useCallback(async (id: string) => {
    if (confirm('Delete this folder? Mindmaps will be moved to root.')) {
      await organization.deleteFolder(id);
    }
  }, [organization]);

  return (
    <div className="mindmap-sidebar-dnd">
      {/* Header */}
      <div className="mindmap-header">
        <h3>Mindmaps</h3>
        {onToggleCollapse && (
          <Button
            icon={IconNames.CHEVRON_LEFT}
            minimal
            onClick={onToggleCollapse}
            title="Collapse sidebar"
          />
        )}
      </div>

      {/* Search */}
      <div className="sidebar-search">
        <InputGroup
          leftIcon={IconNames.SEARCH}
          placeholder="Search mindmaps..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          rightElement={
            searchQuery ? (
              <Button
                icon={IconNames.CROSS}
                minimal
                small
                onClick={() => setSearchQuery('')}
              />
            ) : undefined
          }
        />
      </div>

      <div className="mindmap-content">
        <DragDropContext onDragEnd={onDragEnd}>
          {/* Shared mindmaps - shown first */}
          <div className="mindmap-section">
            <SectionHeader 
              title="Shared" 
              count={sharedMindmaps.length}
              onAdd={() => handleQuickCreate('shared')}
            />
            <StrictModeDroppable droppableId="shared">
                {(provided: DroppableProvided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={`mindmap-list ${snapshot.isDraggingOver ? 'drag-over-shared' : ''}`}
                  >
                    {sharedMindmaps.map((mindmap: any, index: number) => (
                      <Draggable key={mindmap.id} draggableId={mindmap.id} index={index}>
                        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={snapshot.isDragging ? 'dragging' : ''}
                          >
                            <MindmapCard
                              mindmap={mindmap}
                              active={activeMindmapId === mindmap.id}
                              onClick={() => onSelect(mindmap.id)}
                              onDelete={() => handleDeleteMindmap(mindmap.id)}
                              onDuplicate={() => handleDuplicateMindmap(mindmap.id)}
                              onToggleVisibility={() => handleToggleVisibility(mindmap.id)}
                              onRename={(newName) => organization.renameMindmap(mindmap.id, newName)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
          </div>

          {/* Private mindmaps */}
          <div className="mindmap-section">
            <SectionHeader 
              title="Private" 
              count={privateMindmaps.length}
              onAdd={() => handleQuickCreate('private')}
            />
              <StrictModeDroppable droppableId="private">
                {(provided: DroppableProvided, snapshot) => (
                  <div 
                    ref={provided.innerRef} 
                    {...provided.droppableProps}
                    className={`mindmap-list ${snapshot.isDraggingOver ? 'drag-over-private' : ''}`}
                  >
                    {privateMindmaps.map((mindmap: any, index: number) => (
                      <Draggable key={mindmap.id} draggableId={mindmap.id} index={index}>
                        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className={snapshot.isDragging ? 'dragging' : ''}
                          >
                            <MindmapCard
                              mindmap={mindmap}
                              active={activeMindmapId === mindmap.id}
                              onClick={() => onSelect(mindmap.id)}
                              onDelete={() => handleDeleteMindmap(mindmap.id)}
                              onDuplicate={() => handleDuplicateMindmap(mindmap.id)}
                              onToggleVisibility={() => handleToggleVisibility(mindmap.id)}
                              onRename={(newName) => organization.renameMindmap(mindmap.id, newName)}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </StrictModeDroppable>
          </div>
        </DragDropContext>
      </div>

      {/* Create Mindmap Dialog */}
      <Dialog
        isOpen={createDialog}
        onClose={() => setCreateDialog(false)}
        title="Create Mindmap"
      >
        <div className="bp5-dialog-body">
          <FormGroup label="Name" labelFor="mindmap-name">
            <InputGroup
              id="mindmap-name"
              value={mindmapName}
              onChange={(e) => setMindmapName(e.target.value)}
              placeholder="Enter mindmap name..."
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Description" labelFor="mindmap-description">
            <InputGroup
              id="mindmap-description"
              value={mindmapDescription}
              onChange={(e) => setMindmapDescription(e.target.value)}
              placeholder="Optional description..."
            />
          </FormGroup>
          <FormGroup label="Visibility">
            <div className="bp5-control-group">
              <Button
                text="Private"
                icon={IconNames.LOCK}
                active={mindmapVisibility === 'private'}
                onClick={() => setMindmapVisibility('private')}
              />
              <Button
                text="Shared"
                icon={IconNames.PEOPLE}
                active={mindmapVisibility === 'shared'}
                onClick={() => setMindmapVisibility('shared')}
              />
            </div>
          </FormGroup>
        </div>
        <div className="bp5-dialog-footer">
          <div className="bp5-dialog-footer-actions">
            <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button 
              intent={Intent.PRIMARY} 
              onClick={handleCreateMindmap}
              disabled={!mindmapName.trim()}
            >
              Create
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog
        isOpen={createFolderDialog}
        onClose={() => setCreateFolderDialog(false)}
        title="Create Folder"
      >
        <div className="bp5-dialog-body">
          <FormGroup label="Folder Name">
            <InputGroup
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              placeholder="Enter folder name..."
              autoFocus
            />
          </FormGroup>
          <FormGroup label="Type">
            <div className="bp5-control-group">
              <Button
                text="Private"
                icon={IconNames.LOCK}
                active={folderType === 'private'}
                onClick={() => setFolderType('private')}
              />
              <Button
                text="Shared"
                icon={IconNames.PEOPLE}
                active={folderType === 'shared'}
                onClick={() => setFolderType('shared')}
              />
            </div>
          </FormGroup>
        </div>
        <div className="bp5-dialog-footer">
          <div className="bp5-dialog-footer-actions">
            <Button onClick={() => setCreateFolderDialog(false)}>Cancel</Button>
            <Button 
              intent={Intent.PRIMARY} 
              onClick={handleCreateFolder}
              disabled={!folderName.trim()}
            >
              Create
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog
        isOpen={renameDialog.open}
        onClose={() => setRenameDialog({ open: false, id: '', name: '', isFolder: false })}
        title={renameDialog.isFolder ? 'Rename Folder' : 'Rename Mindmap'}
      >
        <div className="bp5-dialog-body">
          <FormGroup label="Name">
            <InputGroup
              value={renameDialog.name}
              onChange={(e) => setRenameDialog({ ...renameDialog, name: e.target.value })}
              placeholder="Enter new name..."
              autoFocus
            />
          </FormGroup>
        </div>
        <div className="bp5-dialog-footer">
          <div className="bp5-dialog-footer-actions">
            <Button onClick={() => setRenameDialog({ open: false, id: '', name: '', isFolder: false })}>
              Cancel
            </Button>
            <Button 
              intent={Intent.PRIMARY} 
              onClick={handleRename}
              disabled={!renameDialog.name.trim()}
            >
              Rename
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
});

export default MindmapSidebarDnD;